/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Switch, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { CustomIconButton, ToolContainerDraggable } from '@opengeoweb/shared';
import type { syncGroupsTypes } from '@opengeoweb/store';
import { syncGroupsSelector } from '@opengeoweb/store';
import { Info } from '@opengeoweb/theme';
import { SyncGroupList } from './SyncGroupList';
import { useCoreTranslation } from '../../utils/i18n';

interface SyncGroupViewerProps {
  onClose: () => void;
  isOpen: boolean;
  syncGroupViewState: syncGroupsTypes.SyncGroupViewState;
  syncGroupAddTarget: (
    payload: syncGroupsTypes.SyncGroupAddTargetPayload,
  ) => void;
  syncGroupRemoveTarget: (
    payload: syncGroupsTypes.SyncGroupRemoveTargetPayload,
  ) => void;
  syncGroupAddGroup: (
    payload: syncGroupsTypes.SyncGroupAddGroupPayload,
  ) => void;
  syncGroupRemoveGroup: (
    payload: syncGroupsTypes.SyncGroupRemoveGroupPayload,
  ) => void;
  onMouseDown?: () => void;
  order?: number;
  isTimeScrollingEnabled?: boolean;
  toggleTimeScroll: (
    payload: syncGroupsTypes.SyncGroupToggleIsTimeScrollingEnabled,
  ) => void;
  toggleSyncGroupsViewer: (isOpen: boolean) => void;
}

export const SyncGroupViewer: React.FC<SyncGroupViewerProps> = ({
  onClose,
  isOpen,
  syncGroupViewState,
  syncGroupAddTarget,
  syncGroupRemoveTarget,
  syncGroupAddGroup,
  syncGroupRemoveGroup,
  onMouseDown,
  order = 0,
  isTimeScrollingEnabled,
  toggleTimeScroll,
  toggleSyncGroupsViewer,
}: SyncGroupViewerProps) => {
  const { t } = useCoreTranslation();

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as Element;
      if (
        target.tagName !== 'INPUT' &&
        target.tagName !== 'TEXTAREA' &&
        (event.key === 's' || event.key === 'S') &&
        !event.getModifierState('Control') &&
        !event.getModifierState('Alt') &&
        !event.getModifierState('AltGraph') &&
        !event.getModifierState('Meta')
      ) {
        toggleTimeScroll({ isTimeScrollingEnabled: !isTimeScrollingEnabled });
      }
      if (
        target.tagName !== 'INPUT' &&
        target.tagName !== 'TEXTAREA' &&
        (event.key === 'y' || event.key === 'Y') &&
        !event.getModifierState('Control') &&
        !event.getModifierState('Alt') &&
        !event.getModifierState('AltGraph') &&
        !event.getModifierState('Meta')
      ) {
        toggleSyncGroupsViewer(!isOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isTimeScrollingEnabled,
    toggleTimeScroll,
    toggleSyncGroupsViewer,
    isOpen,
  ]);

  const handleToggle = (
    groupCategory: string,
    groupId: string,
    sourceId: string,
  ): void => {
    const { selected } = syncGroupViewState[groupCategory].groups.find(
      (groupObject) => groupObject.id === groupId,
    ) as syncGroupsTypes.Group;
    const isChecked = selected.includes(sourceId);
    if (isChecked) {
      syncGroupRemoveTarget({
        groupId: String(groupId),
        targetId: String(sourceId),
        origin: 'user',
      });
    } else {
      const { groups } = syncGroupViewState[groupCategory];
      for (const group of groups) {
        if (group.id !== groupId && group.selected.includes(sourceId)) {
          syncGroupRemoveTarget({
            groupId: String(group.id),
            targetId: String(sourceId),
            origin: 'user',
          });
        }
      }
      syncGroupAddTarget({
        groupId: String(groupId),
        targetId: String(sourceId),
        origin: 'user',
      });
    }
  };

  const addNewGroup = (groupCategory: string, groupId: string): void => {
    syncGroupsSelector.groupTypes.forEach(({ title, syncType, groupType }) => {
      if (groupCategory === groupType) {
        syncGroupAddGroup({
          groupId: String(groupId + Math.floor(Math.random() * 1000)),
          title,
          type: syncType,
          origin: 'user',
        });
      }
    });
  };

  const removeGroup = (groupCategory: string, groupId: string): void => {
    syncGroupsSelector.groupTypes.forEach(({ groupType }) => {
      if (groupCategory === groupType) {
        syncGroupRemoveGroup({ groupId: String(groupId) });
      }
    });
  };

  if (!syncGroupViewState) {
    return null;
  }

  return (
    <ToolContainerDraggable
      data-testid="syncGroupViewer"
      isOpen={isOpen}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      startSize={{ width: 349, height: 800 }}
      title={t('syncgroups-title')}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Typography sx={{ fontSize: 12 }}>
          {t('workspace-time-scroll-off')}
        </Typography>
        <Switch
          checked={isTimeScrollingEnabled}
          onChange={() =>
            toggleTimeScroll({
              isTimeScrollingEnabled: !isTimeScrollingEnabled,
              origin: 'user',
            })
          }
          slotProps={{
            input: { 'aria-label': 'workspace time scroll toggle' },
          }}
        />
        <Typography sx={{ fontSize: 12 }}>
          {t('workspace-time-scroll-on')}
        </Typography>
        <Tooltip
          placement="bottom-start"
          title={t('workspace-time-scroll-info')}
        >
          <CustomIconButton aria-label="time scroll info" variant="flat">
            <Info />
          </CustomIconButton>
        </Tooltip>
      </Box>

      <div>
        <SyncGroupList
          addNewGroup={(groupId): void => addNewGroup('zoompane', groupId)}
          handleToggle={(groupId, sourceId): void =>
            handleToggle('zoompane', groupId, sourceId!)
          }
          removeGroup={(groupId): void => removeGroup('zoompane', groupId)}
          title={t('syncgroups-zoom')}
          viewStateData={syncGroupViewState.zoompane}
        />
        <SyncGroupList
          addNewGroup={(groupId): void => addNewGroup('timeslider', groupId)}
          handleToggle={(groupId, sourceId): void =>
            handleToggle('timeslider', groupId, sourceId!)
          }
          removeGroup={(groupId): void => removeGroup('timeslider', groupId)}
          title={t('syncgroups-timeslider')}
          viewStateData={syncGroupViewState.timeslider}
        />
        <SyncGroupList
          addNewGroup={(groupId): void => addNewGroup('level', groupId)}
          handleToggle={(groupId, sourceId): void =>
            handleToggle('level', groupId, sourceId!)
          }
          removeGroup={(groupId): void => removeGroup('level', groupId)}
          title={t('syncgroups-level')}
          viewStateData={syncGroupViewState.level}
        />
      </div>
    </ToolContainerDraggable>
  );
};
